import React from 'react';
import BudgetRow from './BudgetRow';
import BudgetMarginRow from './BudgetMarginRow';

const SECTION_LABELS = {
  PRODUCT_SALES: 'Outgoing Invoices / Credit Notes',
  PRODUCT_PURCHASE: 'Incoming Invoices / Debit Notes',
  FREIGHT: 'Freight',
  COMMISSIONS: 'Commissions',
  PURCHASING_COSTS: 'Purchasing Costs',
  LAYTIME: 'Laytime Calculations',
  FINANCING: 'Financing',
  OTHER_EXPENSES: 'Other Expected Expenses',
  BELOW_GROSS_ADJUSTMENTS: 'Below-Gross Adjustments',
};

const DEFAULT_ENTRY_MODES = {
  PRODUCT_SALES: 'PER_UNIT',
  PRODUCT_PURCHASE: 'PER_UNIT',
  FREIGHT: 'PER_UNIT',
  COMMISSIONS: 'PER_UNIT',
  PURCHASING_COSTS: 'PER_UNIT',
  LAYTIME: 'PER_UNIT',
  FINANCING: 'BUDGET_AMOUNT',
  OTHER_EXPENSES: 'PER_UNIT',
  BELOW_GROSS_ADJUSTMENTS: 'BUDGET_AMOUNT',
};

const NO_PERCENT_SECTIONS = new Set(['PRODUCT_SALES', 'PRODUCT_PURCHASE']);

const BudgetSection = ({
  sectionCode,
  lines,
  quantity,
  outgoingSubtotal,
  onLineChange,
  onAddRow,
  onRemoveRow,
  isBreakdownChild,
  hideHeader,
}) => {
  const dataRows = lines.filter((l) => !l.is_subtotal && !l.is_margin_row);
  const subtotalRow = lines.find((l) => l.is_subtotal);
  const marginRows = lines.filter((l) => l.is_margin_row);
  const hidePercent = NO_PERCENT_SECTIONS.has(sectionCode);

  const formatAmount = (val) => {
    if (val == null || val === '') return '0.00';
    return parseFloat(val).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatPercent = (val) => {
    if (val == null || val === '') return '0.00';
    return parseFloat(val).toFixed(2);
  };

  if (isBreakdownChild) {
    return (
      <div className="budget-child-panel">
        <div className="budget-child-panel-header">
          <h4>{SECTION_LABELS[sectionCode] || sectionCode}</h4>
        </div>
        <div className="budget-grid-container">
          <table className="budget-grid">
            <thead>
              <tr className="budget-column-header">
                <th className="col-type">Type</th>
                <th className="col-partner">Partner</th>
                <th className="col-description">Description</th>
                <th className="col-amount-per-unit">Amount per unit</th>
                <th className="col-percent">%</th>
                <th className="col-budget-amount">Budget amount</th>
                <th className="col-actions"></th>
              </tr>
            </thead>
            <tbody>
              {dataRows.map((line) => (
                <BudgetRow
                  key={line.id}
                  line={line}
                  sectionCode={sectionCode}
                  quantity={quantity}
                  outgoingSubtotal={outgoingSubtotal}
                  onChange={onLineChange}
                  onRemove={onRemoveRow}
                />
              ))}
              <tr className="add-row-tr">
                <td colSpan="7">
                  <button
                    className="add-row-btn"
                    onClick={() => onAddRow(sectionCode, DEFAULT_ENTRY_MODES[sectionCode] || 'PER_UNIT')}
                  >
                    <i className="fas fa-plus"></i> Add Row
                  </button>
                </td>
              </tr>
              {subtotalRow && (
                <tr className="subtotal-row">
                  <td className="subtotal-label">{subtotalRow.line_label}</td>
                  <td></td>
                  <td></td>
                  <td className="amount-cell">{formatAmount(subtotalRow.amount_per_unit)}</td>
                  <td className="percent-cell">
                    {hidePercent ? null : <>{formatPercent(subtotalRow.percent_of_revenue)}%</>}
                  </td>
                  <td className="amount-cell">{formatAmount(subtotalRow.budget_amount)}</td>
                  <td></td>
                </tr>
              )}
              {marginRows.map((mr) => (
                <BudgetMarginRow
                  key={mr.id}
                  line={mr}
                  isGrossMargin={mr.line_label.toLowerCase().includes('gross')}
                  sectionCode={sectionCode}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  const hasEffectCol = sectionCode === 'BELOW_GROSS_ADJUSTMENTS';
  const colCount = hasEffectCol ? 8 : 7;

  return (
    <>
      {dataRows.map((line) => (
        <BudgetRow
          key={line.id}
          line={line}
          sectionCode={sectionCode}
          quantity={quantity}
          outgoingSubtotal={outgoingSubtotal}
          onChange={onLineChange}
          onRemove={onRemoveRow}
        />
      ))}

      <tr className="add-row-tr">
        <td colSpan={colCount}>
          <button
            className="add-row-btn"
            onClick={() => onAddRow(sectionCode, DEFAULT_ENTRY_MODES[sectionCode] || 'PER_UNIT')}
          >
            <i className="fas fa-plus"></i> Add Row
          </button>
        </td>
      </tr>

      {subtotalRow && (
        <tr className="subtotal-row">
          <td className="subtotal-label">{subtotalRow.line_label}</td>
          {hasEffectCol && <td></td>}
          <td></td>
          <td></td>
          <td className="amount-cell">{formatAmount(subtotalRow.amount_per_unit)}</td>
          <td className="percent-cell">
            {hidePercent ? null : <>{formatPercent(subtotalRow.percent_of_revenue)}%</>}
          </td>
          <td className="amount-cell">{formatAmount(subtotalRow.budget_amount)}</td>
          <td></td>
        </tr>
      )}

      {marginRows.map((mr) => (
        <BudgetMarginRow
          key={mr.id}
          line={mr}
          isGrossMargin={mr.line_label.toLowerCase().includes('gross')}
          sectionCode={sectionCode}
        />
      ))}
    </>
  );
};

export default BudgetSection;
